import React from 'react';
import { GraduationCapIcon, MonitorIcon, MedalIcon } from 'lucide-react';
import { BridgeTemplate } from '../data/bridgeTemplates';
interface V9BridgeLayoutProps {
  template: BridgeTemplate;
  onCtaClick: () => void;
  submitting: boolean;
}
export function V9BridgeLayout({
  template,
  onCtaClick,
  submitting
}: V9BridgeLayoutProps) {
  return <div className="min-h-screen flex flex-col font-sans bg-white" data-id="element-598" data-mp="2j7lf46zbbjq9">
      {/* Header */}
      <header className="w-full bg-white py-4 px-6 md:px-12 flex items-center justify-end" data-id="element-599" data-mp="2x74g8ftphut2">
        <nav className="flex items-center gap-6 md:gap-8 text-sm font-bold" data-id="element-600" data-mp="2t6kmznc97bc7">
          <a href="#" className="text-[#d68c45] hover:text-[#b87535] transition-colors" data-id="element-601" data-mp="36xfmwa4ryi3n">
            Home
          </a>
          <a href="#" className="text-gray-700 hover:text-gray-900 transition-colors" data-id="element-602" data-mp="321qnq7wo337v">
            About
          </a>
          <a href="#" className="text-gray-700 hover:text-gray-900 transition-colors" data-id="element-603" data-mp="3byv8c3av6qmk">
            Blog
          </a>
          <a href="#" className="text-gray-700 hover:text-gray-900 transition-colors" data-id="element-604" data-mp="1k1n13knu8xoa">
            Contact Us
          </a>
        </nav>
      </header>

      {/* Hero Section */}
      <section className="relative w-full py-16 md:py-24 lg:py-32 border-b-4 border-[#7a2e39]" data-id="element-605" data-mp="1t2ulcytirxzd">
        {/* Background Image & Overlay */}
        <div className="absolute inset-0 z-0 bg-cover bg-center bg-no-repeat" style={{
        backgroundImage: 'url("https://cdn.magicpatterns.com/uploads/v6k97Rs1dMhQNyF19TAJDC/image.png")' // Using the mockup image as a placeholder for the background, though ideally we'd have just the background image. I'll use a generic dark overlay to ensure text readability.
      }} data-id="element-606" data-mp="1y0weydoknfp3">
          <div className="absolute inset-0 bg-[#2a3045]/90 mix-blend-multiply" data-id="element-607" data-mp="2gq2r1huv191x"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-2 gap-12 items-center" data-id="element-608" data-mp="2lonpbbqjxinq">
          {/* Left: Hero Copy */}
          <div className="text-white max-w-lg" data-id="element-609" data-mp="gqpdtf81dp5u">
            <div className="flex items-center gap-2 mb-6" data-id="element-610" data-mp="gntdxlcmoqer">
              <h1 className="text-4xl md:text-5xl font-serif font-bold tracking-tight" data-id="element-611" data-mp="vfc4gd3463cd">
                UNIVERSITY <span className="text-[#c83a42]" data-id="element-612" data-mp="qn26qavrbyzj">HQ</span>
              </h1>
              <GraduationCapIcon className="w-8 h-8 text-[#c83a42] -mt-4 -ml-2" data-id="element-613" data-mp="14eh1fez11omf" />
            </div>
            <p className="text-lg md:text-xl leading-relaxed text-gray-200" data-id="element-614" data-mp="10eex0y1wqh3g">
              Your headquarters for higher education. Explore the{' '}
              <span className="text-[#e27b7f] font-semibold" data-id="element-615" data-mp="1ebbxzsbqpey2">
                best accredited online colleges
              </span>
              , compare programs, and find the right fit for your goals.
            </p>
          </div>

          {/* Right: Floating Card */}
          <div className="bg-white rounded-xl p-8 md:p-12 shadow-2xl max-w-md mx-auto lg:mx-0 lg:ml-auto text-center" data-id="element-616" data-mp="3b6zwbu39t5ob">
            <h2 className="text-3xl font-bold text-[#1f2944] mb-3" data-id="element-617" data-mp="1qofm0akzv2fc">
              {template.cardHeadline}
            </h2>
            <p className="text-[#7a2e39] font-bold mb-8" data-id="element-618" data-mp="1gxuh65eq91nz">
              {template.cardSubcopy}
            </p>
            <button onClick={onCtaClick} disabled={submitting} className="w-full bg-[#7a2e39] hover:bg-[#63242d] text-white font-bold py-4 px-6 rounded-lg transition-colors flex items-center justify-center gap-2 text-lg disabled:opacity-70" data-id="element-619" data-mp="1lwnvg01u8wp7">
              {submitting ? 'Loading...' : template.ctaLabel}
            </button>
            <p className="text-sm text-gray-500 mt-6 leading-relaxed" data-id="element-620" data-mp="y1cui2eqjryl">
              {template.cardFootnote}
            </p>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="w-full py-20 md:py-28 bg-white" data-id="element-621" data-mp="2sb6potlfm8gh">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-2 gap-16 lg:gap-24" data-id="element-622" data-mp="2xat3b4569qpt">
          {/* Left: About Copy */}
          <div data-id="element-623" data-mp="x7qr8su83mv2">
            <div className="text-[#7a2e39] text-sm font-bold tracking-[0.2em] uppercase mb-4" data-id="element-624" data-mp="t89m60ipkrqf">
              {template.eyebrow}
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-[#1f2944] leading-tight mb-6" data-id="element-625" data-mp="o7vcke1gl9qa">
              {template.headlineLead} {template.headlineHighlight}
            </h2>
            <div className="w-12 h-1 bg-[#7a2e39] mb-8" data-id="element-626" data-mp="ebigxamcslsw"></div>
            <p className="text-lg text-gray-600 leading-relaxed" data-id="element-627" data-mp="ectollix6dpa">
              {template.subhead}
            </p>
          </div>

          {/* Right: Features List */}
          <div className="flex flex-col justify-center" data-id="element-628" data-mp="5j6tzyqg7gho">
            <div className="space-y-6" data-id="element-629" data-mp="k409afx4wp0">
              {/* Feature 1 */}
              <div className="flex items-center gap-6 pb-6 border-b border-gray-100" data-id="element-630" data-mp="hra4a00bg0v">
                <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0" data-id="element-631" data-mp="3rrzouc8tsg4p">
                  <GraduationCapIcon className="w-6 h-6 text-[#1f2944]" data-id="element-632" data-mp="3cmvnfcpvzae2" />
                </div>
                <div className="text-lg text-gray-800" data-id="element-633" data-mp="3cp84zorqfiql">
                  300+ top-ranked accredited online programs
                </div>
              </div>
              {/* Feature 2 */}
              <div className="flex items-center gap-6 pb-6 border-b border-gray-100" data-id="element-634" data-mp="3miyqqlwh5f2l">
                <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0" data-id="element-635" data-mp="3rjcv9tyva6vv">
                  <MonitorIcon className="w-6 h-6 text-[#1f2944]" data-id="element-636" data-mp="2tujbby9fkuqq" />
                </div>
                <div className="text-lg text-gray-800" data-id="element-637" data-mp="2ytcqdv4d8tt0">
                  No SAT or ACT required at many schools
                </div>
              </div>
              {/* Feature 3 */}
              <div className="flex items-center gap-6 pb-6 border-b border-gray-100" data-id="element-638" data-mp="38ml4kzgzwphs">
                <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0" data-id="element-639" data-mp="2b5d00wayv5ey">
                  <MedalIcon className="w-6 h-6 text-[#1f2944]" data-id="element-640" data-mp="2g34nne4kw5dc" />
                </div>
                <div className="text-lg text-gray-800" data-id="element-641" data-mp="282p0hc3q2kwn">
                  Military-friendly options available
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>;
}